import React, {
    useEffect,
    useRef,
    useState
} from 'react';
import { SwiperSlide } from 'swiper/react';

import {
    Footer,
    Header,
    ShareButtons
} from '$partials/global';
import {
    Carousel,
    CarouselArrowNavigation,
    CarouselWrapper
} from '$partials/base/carousel';
import { EventListingItem } from '$partials/items';
import { Media } from '$partials/media';
import { ModuleDispatcher } from '$partials/modules';
import { usePage } from '$contexts/page.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {React.FC<any>}
 */
const EventDetailPageAside = ({
    eventLocationDetailPages = null,
    externalCtaCssClass = '',
    externalCtaMobileUrlOverride = '',
    externalUrl = '',
    membersOnly = false,
    occurrences = null,
    priceText = '',
    whenText = ''
}) => {
    const { page } = usePage();

    // misc
    const dateFormatter = new Intl.DateTimeFormat(
        'en-GB',
        {
            day: 'numeric',
            month: 'short',
            weekday: 'short'
        }
    );

    const listFormatter = new Intl.ListFormat(
        'en',
        {
            type: 'conjunction',
            style: 'long'
        }
    );

    // render
    return (
        <aside className='sm:sticky sm:top-0 flex flex-col sm:pt-40'>
            {/* Date */}
            {whenText && (
                <div className='border-t border-border-default sm:border-none'>
                    <div className='col-padding py-16'>
                        <dt className='type-style-single-line-eyebrow'>
                            When
                        </dt>

                        <dd className='type-style-sans-body mt-8'>
                            {whenText}
                        </dd>
                    </div>
                </div>
            )}

            {/* Location */}
            {(eventLocationDetailPages?.length > 0) && (
                <div className='border-t border-border-default'>
                    <div className='col-padding py-16'>
                        <dt className='type-style-single-line-eyebrow'>
                            Where
                        </dt>

                        <dd className='type-style-sans-body mt-8'>
                            <address className='not-italic'>
                                {listFormatter.formatToParts(eventLocationDetailPages.map(el => el.title)).map((itm, idx) => {
                                    if (itm.type === 'element') {
                                        const location = eventLocationDetailPages.find(el => el.title === itm.value);

                                        return (
                                            <a
                                                className='underline underline-offset-2'
                                                href={location?.url}
                                                key={idx}>
                                                {itm.value}
                                            </a>
                                        );
                                    } else {
                                        return (
                                            <span key={idx}>{itm.value}</span>
                                        );
                                    }
                                })}
                            </address>
                        </dd>
                    </div>
                </div>
            )}

            {/* Tickets */}
            {(priceText || membersOnly) && (
                <div className='border-t border-border-default'>
                    <div className='col-padding py-16'>
                        <dt className='type-style-single-line-eyebrow'>
                            Tickets
                        </dt>

                        <dd className='type-style-sans-body mt-8'>
                            {membersOnly ? 'Members only' : priceText}
                        </dd>
                    </div>
                </div>
            )}

            {/* Booking Button(s) */}
            <div className='border-t border-border-default'>
                <div className='col-padding py-16'>
                    {(occurrences?.length > 1) ? (
                        <>
                            <dt className='type-style-single-line-eyebrow'>
                                Book Your Place
                            </dt>

                            <dd className='mt-8'>
                                <ul className='flex flex-wrap gap-8'>
                                    {occurrences?.map((oc, idx) => {
                                        if (idx < 5) {
                                            return (
                                                <li key={idx}>
                                                    {externalCtaMobileUrlOverride ? (
                                                        <>
                                                            <a
                                                                className='sm:hidden button-outcome'
                                                                data-analytics-action='click'
                                                                data-analytics-context='book_event'
                                                                data-analytics-destination={externalCtaMobileUrlOverride}
                                                                data-analytics-id={`${page?.id}_event_occurrence_book_cta_${idx}`}
                                                                data-analytics-label={`${page?.title} ${dateFormatter.format(new Date(oc.dateStart))}`}
                                                                data-analytics-location='aside'
                                                                data-analytics-module='event_occurrence_book_cta'
                                                                href={externalCtaMobileUrlOverride}
                                                                rel='noreferrer'
                                                                target='_blank'>
                                                                {dateFormatter.format(new Date(oc.dateStart))}
                                                            </a>
                                                            <a
                                                                className={`hidden sm:block button-outcome${externalCtaCssClass ? ' ' + externalCtaCssClass: ''}`}
                                                                data-analytics-action='click'
                                                                data-analytics-context='book_event'
                                                                data-analytics-destination={externalUrl}
                                                                data-analytics-id={`${page?.id}_event_occurrence_book_cta_${idx}`}
                                                                data-analytics-label={`${page?.title} ${dateFormatter.format(new Date(oc.dateStart))}`}
                                                                data-analytics-location='aside'
                                                                data-analytics-module='event_occurrence_book_cta'
                                                                href={externalUrl}
                                                                rel='noreferrer'
                                                                target='_blank'>
                                                                {dateFormatter.format(new Date(oc.dateStart))}
                                                            </a>
                                                        </>
                                                    ) : (
                                                        <a
                                                            className={`button-outcome${externalCtaCssClass ? ' ' + externalCtaCssClass: ''}`}
                                                            data-analytics-action='click'
                                                            data-analytics-context='book_event'
                                                            data-analytics-destination={externalUrl}
                                                            data-analytics-id={`${page?.id}_event_occurrence_book_cta_${idx}`}
                                                            data-analytics-label={`${page?.title} ${dateFormatter.format(new Date(oc.dateStart))}`}
                                                            data-analytics-location='aside'
                                                            data-analytics-module='event_occurrence_book_cta'
                                                            href={externalUrl}
                                                            rel='noreferrer'
                                                            target='_blank'>
                                                            {dateFormatter.format(new Date(oc.dateStart))}
                                                        </a>
                                                    )}
                                                </li>
                                            );
                                        } else if (idx === 5) {
                                            return (
                                                <li key={idx}>
                                                    {externalCtaMobileUrlOverride ? (
                                                        <>
                                                            <a
                                                                className='sm:hidden button-outcome'
                                                                href={externalCtaMobileUrlOverride}
                                                                rel='noreferrer'
                                                                target='_blank'>
                                                                See more
                                                            </a>
                                                            <a
                                                                className={`hidden sm:block button-outcome${externalCtaCssClass ? ' ' + externalCtaCssClass: ''}`}
                                                                href={externalUrl}
                                                                rel='noreferrer'
                                                                target='_blank'>
                                                                See more
                                                            </a>
                                                        </>
                                                    ) : (
                                                        <a
                                                            className={`button-outcome${externalCtaCssClass ? ' ' + externalCtaCssClass: ''}`}
                                                            href={externalUrl}
                                                            rel='noreferrer'
                                                            target='_blank'>
                                                            See more
                                                        </a>
                                                    )}
                                                </li>
                                            );
                                        } else {
                                            return null;
                                        }
                                    }).filter(Boolean)}
                                </ul>
                            </dd>
                        </>
                    ) : (
                        <>
                            <dt className='sr-only type-style-single-line-eyebrow'>
                                Book Your Place
                            </dt>

                            <dd>
                                {occurrences?.map((_oc, idx) => {
                                    return (
                                        <a
                                            aria-disabled={!externalUrl}
                                            className={`button-primary${externalUrl ? '' : ' opacity-50'}${externalCtaCssClass ? ' ' + externalCtaCssClass: ''}`}
                                            href={externalUrl}
                                            inert={!externalUrl}
                                            key={idx}
                                            rel='noreferrer'
                                            target='_blank'>
                                            Book Tickets
                                        </a>
                                    );
                                })}
                            </dd>
                        </>
                    )}
                </div>
            </div>

            {/* Social */}
            <div className='border-t border-border-default'>
                <div className='col-padding py-16'>
                    <dt className='type-style-single-line-eyebrow'>
                        Share
                    </dt>

                    <dd className='mt-8'>
                        <ShareButtons />
                    </dd>
                </div>
            </div>
        </aside>
    );
};




/**
 * @type {import('$types/components/pages.d.ts').EventDetailPage}
 */
const EventDetailPage = (props) => {
    // misc
    const page = props.data.page;

    // references
    const currentInnerWidth = useRef(0);

    // state
    const [isMobileScreen, setIsMobileScreen] = useState(true);

    // methods
    const handleResize = () => {
        if (currentInnerWidth.current !== window.innerWidth) {
            currentInnerWidth.current = window.innerWidth;

            if (window.innerWidth < 768) {
                if (!isMobileScreen) {
                    setIsMobileScreen(true);
                }
            } else {
                if (isMobileScreen) {
                    setIsMobileScreen(false);
                }
            }
        }
    };

    // hooks
    useEffect(() => {
        window.addEventListener('resize', handleResize, { passive: true });
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, [
        isMobileScreen,
        setIsMobileScreen
    ]);

    // render
    return (
        <>
            <Header
                {...props?.data?.header}
                pageType={page.__typename} />

            <main id='main-content'>
                <div className='layout-container layout-site-outer-padding pb-8 md:pb-16'>
                    {/* Main Content */}
                    <div className='col-wrapper-breakout flex flex-wrap rounded-lg bg-surface-panel-yellow'>
                        {/* Left Col */}
                        <div className='w-full sm:w-9/12 md:w-8/12 flex flex-col gap-24 pb-40 pt-16 sm:border-r border-border-default'>
                            <section className='flex flex-col gap-24'>
                                {/* Tag */}
                                {page?.eventTypeDetailPages?.length > 0 && (
                                    <ul className='flex flex-wrap gap-8 items-start col-padding'>
                                        {page.eventTypeDetailPages.map((ev, idx) => {
                                            return (
                                                <li key={idx}>
                                                    <a
                                                        className='tag-primary'
                                                        href={ev.url}>
                                                        {ev.title}
                                                    </a>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}

                                <div className='flex flex-col gap-8 col-padding'>
                                    {/* Title */}
                                    <h1 className='type-style-sans-display'>
                                        {page.title}
                                    </h1>

                                    {/* Facilitator */}
                                    {page?.facilitator && (
                                        <p className='type-style-sans-body'>
                                            With {page.facilitator}
                                        </p>
                                    )}
                                </div>

                                {/* Media Asset */}
                                <div>
                                    {page.mediaAsset && (
                                        <figure className='col-padding'>
                                            <Media
                                                {
                                                    ...{
                                                        ...page.mediaAsset,
                                                        ...(page.mediaAsset?.image ? {
                                                            image: {
                                                                ...page.mediaAsset?.image,
                                                                containerClassName: 'rounded-lg overflow-hidden pointer-events-none select-none'
                                                            }
                                                        } : {} ),
                                                        ...(page.mediaAsset?.videoEmbed ? {
                                                            videoEmbed: {
                                                                ...page.mediaAsset?.videoEmbed,
                                                                autoPlay: page.mediaAssetAutoplay,
                                                                poster: page.mediaAsset?.videoEmbed?.poster || page.mediaAsset?.videoEmbed?.embed?.thumbnailUrl
                                                            }
                                                        } : {} ),
                                                        ...(page.mediaAsset?.video ? {
                                                            video: {
                                                                ...page.mediaAsset?.video,
                                                                autoPlay: page.mediaAssetAutoplay,
                                                                showControls: !page.mediaAssetAutoplay,
                                                                loop: page.mediaAssetAutoplay,
                                                            }
                                                        } : {} )
                                                    }
                                                } />

                                            {page.mediaAssetCaption && (
                                                <figcaption className='mt-8 type-style-sans-body text-text-default-secondary'>
                                                    {page.mediaAssetCaption}
                                                </figcaption>
                                            )}
                                        </figure>
                                    )}
                                </div>
                            </section>

                            {/* Aside - Mobile */}
                            <div
                                className='sm:hidden w-full'
                                inert={!isMobileScreen}>
                                <EventDetailPageAside
                                    eventLocationDetailPages={page?.eventLocationDetailPages}
                                    externalCtaCssClass={page?.externalCtaCssClass}
                                    externalCtaMobileUrlOverride={page?.externalCtaMobileUrlOverride}
                                    externalUrl={page?.externalUrl}
                                    membersOnly={page?.membersOnly}
                                    occurrences={page?.occurrences}
                                    priceText={page?.priceText}
                                    whenText={page?.whenText} />
                            </div>

                            {/* Modules*/}
                            <div className='flex flex-col gap-32'>
                                {page?.modules?.map((module, idx) => (
                                    <ModuleDispatcher
                                        {...module}
                                        key={module?.id + '-' + idx} />
                                ))}
                            </div>
                        </div>

                        {/* Aside - Tablet / Desktop*/}
                        <div
                            className='hidden sm:block w-full sm:w-3/12 md:w-4/12'
                            inert={isMobileScreen}>
                            <EventDetailPageAside
                                eventLocationDetailPages={page?.eventLocationDetailPages}
                                externalCtaCssClass={page?.externalCtaCssClass}
                                externalCtaMobileUrlOverride={page?.externalCtaMobileUrlOverride}
                                externalUrl={page?.externalUrl}
                                membersOnly={page?.membersOnly}
                                occurrences={page?.occurrences}
                                priceText={page?.priceText}
                                whenText={page?.whenText} />
                        </div>
                    </div>

                    {/* Related Events */}
                    {(page.relatedEvents?.edges?.length > 0) && (
                        <section className='col-wrapper-breakout bg-surface-panel-yellow rounded-lg mt-8 md:mt-16 pt-24 pb-8 md:pb-24'>
                            <div className='col-padding'>
                                <h2 className='type-style-sans-h1'>
                                    More like this
                                </h2>

                                <p className='type-style-sans-h3 mt-8'>
                                    Join the very best in Leadership
                                </p>
                            </div>

                            <Carousel swiperConfig={{
                                a11y: { enabled: true },
                                slidesPerView: 1.2,
                                slidesOffsetBefore: 8,
                                slidesOffsetAfter: 8,
                                breakpoints: {
                                    992: {
                                        slidesPerView: 2.35,
                                    },
                                    1440: {
                                        slidesPerView: 3.15,
                                        slidesOffsetBefore: 16,
                                        slidesOffsetAfter: 16
                                    }
                                },
                                spaceBetween: 8,
                                grabCursor: false,
                            }}>
                                <div className='col-padding mb-16 mt-20 md:mt-12 flex justify-end'>
                                    <div className='flex'>
                                        <a
                                            className='button-outcome'
                                            href={page.parent?.url}>
                                            View all
                                        </a>

                                        {/* Desktop */}
                                        {page.relatedEvents.edges?.length > 3 && (
                                            <div className='hidden md:block ml-8 md:ml-16'>
                                                <CarouselArrowNavigation />
                                            </div>
                                        )}

                                        {/* Tablet */}
                                        {page.relatedEvents.edges?.length > 2 && (
                                            <div className='hidden sm:block md:hidden ml-8 md:ml-16'>
                                                <CarouselArrowNavigation />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className='-mx-4 md:-mx-8 overflow-hidden'>
                                    <CarouselWrapper>
                                        {page.relatedEvents.edges.map((item, idx) => {
                                            if (!item.node) { return null; }

                                            return (
                                                <SwiperSlide
                                                    aria-roledescription='slide'
                                                    className='select-none cursor-pointer flex flex-wrap mt-4'
                                                    key={item.node.id + '-' + idx}
                                                    role='group'>
                                                    <EventListingItem { ...item.node } />
                                                </SwiperSlide>
                                            );
                                        }).filter(Boolean)}
                                    </CarouselWrapper>
                                </div>
                            </Carousel>
                        </section>
                    )}
                </div>
            </main>

            <Footer
                {...props?.data?.footer}
                exploreHide={true}
                magazineHide={true}
                pageType={page.__typename} />
        </>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default EventDetailPage;
